/**
 * PasskeyVerify - Component for verifying with a passkey
 *
 * Used for step-up verification flow:
 * 1. Call POST /auth/webauthn/authenticate/options to get challenge
 * 2. Call navigator.credentials.get() with publicKey options
 * 3. Send assertion to POST /auth/webauthn/authenticate/verify
 * 4. Return step-up token on success
 */

import { useState, useEffect } from 'react'
import { Key, AlertCircle, Fingerprint, Loader2 } from 'lucide-react'
import { Button } from './ui/Button'
import { getApiUrl } from '../api/client'
import {
  isPasskeyAvailable,
  prepareRequestOptions,
  encodeAuthenticationResponse,
  type PublicKeyCredentialRequestOptionsJSON,
} from '../utils/webauthn'

interface PasskeyVerifyProps {
  /** The step-up challenge ID */
  challengeId: string
  /** Callback when verification succeeds with token */
  onSuccess: (token: string) => void
  /** Callback when verification fails */
  onError?: (error: string) => void
  /** Callback to go back to method selection */
  onBack?: () => void
  /** Whether there are other methods available */
  hasOtherMethods?: boolean
  /** Auto-trigger verification on mount */
  autoTrigger?: boolean
}

interface AuthenticateOptionsResponse {
  options: PublicKeyCredentialRequestOptionsJSON
}

interface AuthenticateVerifyResponse {
  success: boolean
  token?: string
  error?: string
}

export function PasskeyVerify({
  challengeId,
  onSuccess,
  onError,
  onBack,
  hasOtherMethods = false,
  autoTrigger = false,
}: PasskeyVerifyProps) {
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)

  // Check availability on mount
  useEffect(() => {
    isPasskeyAvailable().then((available) => {
      setIsAvailable(available)
      // Auto-trigger if requested and available
      if (autoTrigger && available) {
        handleVerify()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTrigger])

  async function handleVerify() {
    setVerifying(true)
    setError(null)

    try {
      // Step 1: Get authentication options from server
      const optionsResponse = await fetch(
        `${getApiUrl()}/api/stepup/${challengeId}/passkey/options`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!optionsResponse.ok) {
        const data = await optionsResponse.json()
        throw new Error(data.detail || 'Failed to get authentication options')
      }

      const { options }: AuthenticateOptionsResponse =
        await optionsResponse.json()

      // Step 2: Get assertion using WebAuthn API
      const requestOptions = prepareRequestOptions(options)
      const credential = await navigator.credentials.get({
        publicKey: requestOptions,
      })

      if (!credential || !(credential instanceof PublicKeyCredential)) {
        throw new Error('Failed to get credential')
      }

      // Step 3: Send assertion to server for verification
      const encodedAssertion = encodeAuthenticationResponse(credential)
      const verifyResponse = await fetch(
        `${getApiUrl()}/api/stepup/${challengeId}/passkey/verify`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            credential: encodedAssertion,
          }),
        }
      )

      const verifyData: AuthenticateVerifyResponse = await verifyResponse.json()

      if (!verifyResponse.ok || !verifyData.success) {
        throw new Error(verifyData.error || 'Failed to verify passkey')
      }

      if (!verifyData.token) {
        throw new Error('No token received from server')
      }

      // Success! Return token
      onSuccess(verifyData.token)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to verify passkey'

      // Handle specific WebAuthn errors
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError('Passkey verification was cancelled or timed out')
        } else if (err.name === 'SecurityError') {
          setError('Security error - please try again')
        } else {
          setError(message)
        }
      } else {
        setError(message)
      }

      onError?.(message)
    } finally {
      setVerifying(false)
    }
  }

  // Not available
  if (isAvailable === false) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>Passkeys are not available on this device</span>
        </div>
        {hasOtherMethods && onBack && (
          <button
            onClick={onBack}
            className="w-full text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            Try a different method
          </button>
        )}
      </div>
    )
  }

  // Loading availability check
  if (isAvailable === null) {
    return (
      <div className="flex items-center justify-center gap-2 p-4 text-[var(--text-secondary)]">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Checking passkey availability...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Key className="h-5 w-5 text-primary-600" />
        <span className="font-medium text-[var(--text-primary)]">
          Passkey Verification
        </span>
      </div>

      <p className="text-sm text-[var(--text-secondary)]">
        Use your security key, Face ID, Touch ID, or Windows Hello to verify
        your identity.
      </p>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--error)]/30 bg-[var(--error-muted)]/30 p-3 text-sm text-[var(--error)]">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button
        onClick={handleVerify}
        disabled={verifying}
        loading={verifying}
        fullWidth
        className="gap-2"
      >
        <Fingerprint className="h-4 w-4" />
        {verifying ? 'Verifying...' : 'Verify with Passkey'}
      </Button>

      {hasOtherMethods && onBack && (
        <button
          onClick={onBack}
          className="w-full text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          Try a different method
        </button>
      )}
    </div>
  )
}
